import { Cache } from '@/utils/Cache.js';
import { revokeBlobUrl } from '@/utils/blobUrl.js';

const imageDataRenderUrlCacheInstance = new Cache(50);

/**
 * 释放 imageData 临时还原图片 blobUrl。
 * @param {string} url - 需要释放的图片地址。
 * @returns {void}
 */
function revokeImageDataRenderBlobUrl(url) {
  revokeBlobUrl(url);
}

/**
 * 获取缓存中当前 key 对应的原始记录值。
 * @param {string} key - 缓存 key。
 * @returns {string|null} 缓存值。
 */
function getCachedUrl(key) {
  return imageDataRenderUrlCacheInstance.get(key);
}

/**
 * 释放并删除指定 key 的临时缓存。
 * @param {string} key - 缓存 key。
 * @returns {void}
 */
function deleteCachedUrl(key) {
  const cachedUrl = getCachedUrl(key);
  revokeImageDataRenderBlobUrl(cachedUrl);
  imageDataRenderUrlCacheInstance.delete(key);
}

export const imageDataRenderUrlCache = {
  /**
   * 读取 imageData 临时还原图片缓存。
   * @param {string} key - 缓存 key。
   * @returns {string|null} 缓存中的图片地址。
   */
  get(key) {
    return getCachedUrl(key);
  },

  /**
   * 判断 imageData 临时还原图片缓存是否存在。
   * @param {string} key - 缓存 key。
   * @returns {boolean} 缓存是否存在。
   */
  has(key) {
    return imageDataRenderUrlCacheInstance.has(key);
  },

  /**
   * 写入 imageData 临时还原图片缓存，并在替换或淘汰时释放旧 blobUrl。
   * @param {string} key - 缓存 key。
   * @param {string} value - 图片地址。
   * @param {number} [expirationTime] - 过期时间，单位毫秒。
   * @returns {void}
   */
  set(key, value, expirationTime) {
    const oldValue = getCachedUrl(key);
    // 同 key 写入新地址时，先释放旧 blobUrl。
    if (oldValue && oldValue !== value) {
      revokeImageDataRenderBlobUrl(oldValue);
    }

    const queue = imageDataRenderUrlCacheInstance.queue || [];
    const data = imageDataRenderUrlCacheInstance.data || {};
    const willEvict =
      !imageDataRenderUrlCacheInstance.has(key) &&
      queue.length >= imageDataRenderUrlCacheInstance.maxSize;
    const oldestKey = willEvict && queue[0] ? queue[0].key : '';
    const oldestUrl =
      oldestKey && data[oldestKey] ? data[oldestKey].value : null;
    imageDataRenderUrlCacheInstance.set(key, value, expirationTime);
    // Cache 内部淘汰最旧数据后，这里补充释放被淘汰的 blobUrl。
    if (willEvict && oldestKey !== key) {
      revokeImageDataRenderBlobUrl(oldestUrl);
    }
  },

  /**
   * 删除指定 imageData 临时还原图片缓存。
   * @param {string} key - 缓存 key。
   * @returns {void}
   */
  delete(key) {
    deleteCachedUrl(key);
  },

  /**
   * 清空 imageData 临时还原图片缓存，并释放其中的 blobUrl。
   * @returns {void}
   */
  clear() {
    const data = imageDataRenderUrlCacheInstance.data || {};
    Object.keys(data).forEach((key) => {
      revokeImageDataRenderBlobUrl(data[key] && data[key].value);
    });
    imageDataRenderUrlCacheInstance.clear();
  },

  /**
   * 获取 imageData 临时还原图片缓存数量。
   * @returns {number} 当前缓存数量。
   */
  size() {
    return imageDataRenderUrlCacheInstance.size();
  }
};

/**
 * 清理 imageData 临时还原图片缓存。
 * @returns {void}
 */
export function clearImageDataRenderUrlCache() {
  imageDataRenderUrlCache.clear();
}
